import { CopyIcon, ModeIcon } from '@econsult/econsult-ui/dist/assets';
import { Feature, RenderOn } from '@econsult/econsult-ui/dist/components/FlagControlledFeature/index';
import { Paper } from '@econsult/econsult-ui/dist/components/Paper';
import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { Copilot } from 'components/Copilot';
import { SubDivComponent } from 'components/Div/styles';
import GenericModalTrigger from 'components/Modal/GenericModalTrigger';
import RichTextEditor, { convertHtmlStringToText } from 'components/RichTextEditor';
import { SuccessToast } from 'components/Toast';
import Transcriptions from 'components/Transcription/Transcriptions';
import { STRINGS } from 'config';
import { marked } from 'marked';
import { SAVING } from 'model/toolbar/saveToRecord';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { updateSummary } from 'store/action/summary';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { EditInputControls } from './EditInputControls';
import { FeedbackModalWithSummary } from './FeedbackModalWithSummary';
import * as styles from './styles';
import { SummaryComplaintHeading } from './SummaryComplaintHeading';

marked.setOptions({
  breaks: true, // Treat single line breaks as <br>
});

export function preprocessMarkdown(md: string) {
  const lines = md
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  const result: string[] = [];
  let currentHeading: string | null = null;
  let currentBody: string[] = [];
  let currentList: string[] = [];
  let hasContent = false;
  let hasHeadings = false;

  function flush() {
    if (currentHeading) {
      result.push(`<p><strong>${currentHeading}</strong></p>`);
    }
    if (currentList.length) {
      result.push(`<ul>${currentList.join('')}</ul>`);
      currentList = [];
      hasContent = true; // Lists count as content
    }
    if (currentBody.length) {
      result.push(`<p>${currentBody.join(' ')}</p>`);
      currentBody = [];
      hasContent = true; // Body text counts as content
    }
  }

  lines.forEach(line => {
    const headingMatch = /^(?:\*\*|__)(.+?)(?:\*\*|__)$/.exec(line);
    const listMatch = /^-\s+(.*)/.exec(line);

    if (headingMatch) {
      flush();
      const [, heading] = headingMatch;
      currentHeading = heading;
      hasHeadings = true; // Found at least one heading
    } else if (listMatch) {
      const [, item] = listMatch;
      currentList.push(`<li>${item}</li>`);
    } else {
      currentBody.push(line);
    }
  });

  flush();

  // Only return HTML if there's actual content beyond just headings
  // If there are only headings with no body content, return empty string
  // If there are no headings at all, return empty string
  return hasHeadings && hasContent ? result.join('\n') : '';
}

export default function Summary() {
  const {
    copilot: { isQueryRequestSent },
    summary: { currentSummary, error: summaryError },
  } = useAppState();
  const {
    toolbar: { saveToRecordStatus },
  } = useAppState();
  const [editedSummary, setEditedSummary] = useState<string | null>(null);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const transcript = location.state?.transcript || 'No transcript available';

  const handleCopy = () => {
    const formattedSummary = convertHtmlStringToText(editedSummary || currentSummary);
    navigator.clipboard.writeText(formattedSummary);
    toast.success(<SuccessToast message="Summary copied to clipboard" />, { icon: false, autoClose: 6000 });
  };

  const handleSave = () => {
    if (editedSummary !== null) {
      dispatch(updateSummary(editedSummary));
      setEditedSummary(null);
    }
    dispatch({ type: STRINGS.REDUCERS.APP.SET_EDITING_SUMMARY, payload: false });
  };

  const handleDiscard = () => {
    setEditedSummary(null);
    dispatch({ type: STRINGS.REDUCERS.APP.SET_EDITING_SUMMARY, payload: false });
  };

  const handleEditSummaryButton = () => {
    setEditedSummary(currentSummary);
    dispatch({ type: STRINGS.REDUCERS.APP.SET_EDITING_SUMMARY, payload: true });
  };
  const isEditSummaryButtonDisabled = editedSummary !== null || saveToRecordStatus === SAVING || isQueryRequestSent;
  let SummaryView;
  const textEditorValue = currentSummary || 'Huma intelligence is generating the summary...';
  const noContentInEditor = '<p></p>';
  if (!summaryError) {
    SummaryView =
      editedSummary !== null ? (
        <>
          <RichTextEditor value={editedSummary} onChange={setEditedSummary} showToolbar isReadOnly={false} />
          <EditInputControls
            handleSave={handleSave}
            handleDiscard={handleDiscard}
            isSaveButtonDisabled={editedSummary === noContentInEditor}
          />
        </>
      ) : (
        <div id="summary-content" data-automation="Summary-Content-Display">
          <RichTextEditor value={textEditorValue} isReadOnly showToolbar={false} />
        </div>
      );
  }
  if (summaryError) {
    SummaryView = (
      <>
        <div>
          <p id="no-medical-summary-message" data-automation="No-Medical-Summary-Message">
            No relevant medical notes could be extracted from this conversation.
          </p>
        </div>
        <styles.TranscriptContainer data-automation="Transcript-Container">
          <h3 data-automation="Transcript-Heading">Full Consultation Transcript</h3>
          <Transcriptions
            transcriptions={Array.isArray(transcript) ? transcript : transcript.split('\n')}
            interim=""
            data-automation="Transcript-Content"
          />
        </styles.TranscriptContainer>
      </>
    );
  }

  return (
    <SubDivComponent data-automation="Summary-Page-Container">
      <Paper data-automation="Summary-Paper-Wrapper">
        <styles.SummaryHeader data-automation="Summary-Header">
          <div style={{ display: 'flex', flexDirection: 'column', marginRight: '16px' }}>
            <styles.FlexGroup style={{ justifyContent: 'flex-start' }}>
              <styles.SummaryTitle data-automation="Summary-Header-Title">Consultation Summary</styles.SummaryTitle>
            </styles.FlexGroup>
            <SummaryComplaintHeading />
          </div>
          {currentSummary && (
            <>
              <styles.FlexGroup data-automation="Summary-Header-Buttons" style={{ margin: 0 }}>
                <Feature flags={[STRINGS.CUSTOMISATION.FEATURE_FLAGS.SUMMARISER_SEND_FEEDBACK_ENABLED]}>
                  <RenderOn>
                    <div style={{ marginRight: 'auto' }}>
                      <GenericModalTrigger
                        trigger={
                          <styles.FeedbackButton
                            fillStyle="filled"
                            size="small"
                            state="secondary"
                            id="feedback-summary-button"
                            data-automation="Feedback-Summary"
                          >
                            {' '}
                            Help us improve
                          </styles.FeedbackButton>
                        }
                        title="Help us improve"
                      >
                        {closeModal => <FeedbackModalWithSummary closeModal={closeModal} />}
                      </GenericModalTrigger>
                    </div>
                  </RenderOn>
                </Feature>
                <styles.SummaryHeaderButton
                  fillStyle="ghost"
                  size="small"
                  variant="primary"
                  iconPosition="left"
                  icon={<ModeIcon />}
                  id="edit-summary-button"
                  data-automation="Button-Edit-Summary"
                  disabled={isEditSummaryButtonDisabled}
                  onClick={handleEditSummaryButton}
                >
                  Edit
                </styles.SummaryHeaderButton>
                <styles.SummaryHeaderButton
                  fillStyle="ghost"
                  size="small"
                  variant="primary"
                  icon={<CopyIcon />}
                  iconPosition="left"
                  id="copy-summary-button"
                  data-automation="Button-Copy-Summary"
                  onClick={handleCopy}
                >
                  Copy
                </styles.SummaryHeaderButton>
              </styles.FlexGroup>
            </>
          )}
        </styles.SummaryHeader>
        {SummaryView}
      </Paper>
      <Copilot />
    </SubDivComponent>
  );
}
